/**
 * Application routing configuration
 * Defines all routes with automatic error boundaries
 * 
 * Authentication enabled - all routes except login/register are protected
 */
import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from '@components/common';
import { LoadingFallback } from '@components/common/LoadingFallback/LoadingFallback';
import { ProtectedRoute } from '@components/common/ProtectedRoute/ProtectedRoute';
import { PublicRoute } from './PublicRoutes';
import { ROUTES } from '@core/constants/routes.constants';
import { wrapRoute } from './RouteWrapper';
import { LandingLayout, DetailLayout, ViewAllLayout, SuggestedAIToolsLayout, AuthLayout } from '@/components/layout';

// Lazy load pages
const NotFound = lazy(() => import('@features/error/pages/NotFound'));
const Login = lazy(() => import('@features/auth/pages/Login'));
const Register = lazy(() => import('@features/auth/pages/Register'));
const Dashboard = lazy(() => import('@features/dashboard/pages/Dashboard'));
const LandingPage = lazy(() => import('@features/landing/pages/LandingPage'));
const ToolDetailPage = lazy(() => import('@features/tools/pages/ToolDetailPage'));
const AllToolsPage = lazy(() => import('@features/tools/pages/AllToolsPage'));
const CourseDetailPage = lazy(() => import('@features/courses/pages/CourseDetailPage'));
const AllCoursesPage = lazy(() => import('@features/courses/pages/AllCoursesPage'));
const NewsDetailPage = lazy(() => import('@features/news/pages/NewsDetailPage'));
const AllNewsPage = lazy(() => import('@features/news/pages/AllNewsPage'));
const AboutPage = lazy(() => import('@features/about/pages/AboutPage'));
const SuggestedAIToolsPage = lazy(() => import('@features/suggested-ai-tools/pages/SuggestedAIToolsPage'));
const ResponsibleAIPage = lazy(() => import('@features/responsible-ai/pages/ResponsibleAIPage'));
const VideoDetailPage = lazy(() => import('@features/responsible-ai/pages/VideoDetailPage'));

/**
 * Application routes component
 * Authentication enabled - all routes except login/register are protected
 */
export const AppRoutes: React.FC = () => {
  return (
    <ErrorBoundary componentName="Application Routes" showDetails={import.meta.env.DEV}>
      <Suspense fallback={<LoadingFallback fullScreen />}>
        <Routes>
          {/* Login - bare route, renders its own full-screen overlay */}
          <Route
            path={ROUTES.LOGIN}
            element={
              <PublicRoute>
                {wrapRoute(<Login />)}
              </PublicRoute>
            }
          />

          {/* Register still uses AuthLayout */}
          <Route element={<AuthLayout />}>
            <Route
              path={ROUTES.REGISTER}
              element={
                <PublicRoute>
                  {wrapRoute(<Register />)}
                </PublicRoute>
              }
            />
          </Route>

          {/* Protected routes - All other routes require authentication */}
          <Route element={<ProtectedRoute />}>
            {/* Home page - uses LandingLayout with special backgrounds */}
            <Route element={<LandingLayout />}>
              <Route path={ROUTES.HOME} element={wrapRoute(<LandingPage />)} />
            </Route>

            {/* Dashboard */}
            <Route path={ROUTES.DASHBOARD} element={wrapRoute(<Dashboard />)} />

            {/* View All pages - use ViewAllLayout */}
            <Route element={<ViewAllLayout />}>
              <Route path={ROUTES.ABOUT} element={wrapRoute(<AboutPage />)} />
              <Route path={ROUTES.TOOLS} element={wrapRoute(<AllToolsPage />)} />
              <Route path={ROUTES.COURSES} element={wrapRoute(<AllCoursesPage />)} />
              <Route path={ROUTES.NEWS} element={wrapRoute(<AllNewsPage />)} />
              <Route path={ROUTES.RESPONSIBLE_USE_OF_AI} element={wrapRoute(<ResponsibleAIPage />)} />
            </Route>

            {/* Suggested AI Tools page - uses SuggestedAIToolsLayout */}
            <Route element={<SuggestedAIToolsLayout />}>
              <Route path={ROUTES.SUGGESTED_AI_TOOLS} element={wrapRoute(<SuggestedAIToolsPage />)} />
            </Route>

            {/* Detail pages - use DetailLayout */}
            <Route element={<DetailLayout />}>
              {/* Tool detail routes - use nid directly */}
              <Route path={`${ROUTES.TOOLS}/:nid`} element={wrapRoute(<ToolDetailPage />)} />
              {/* Course detail routes - use nid directly */}
              <Route path="/courses/:nid" element={wrapRoute(<CourseDetailPage />)} />
              {/* News detail routes - use nid directly */}
              <Route path="/news/:nid" element={wrapRoute(<NewsDetailPage />)} />
              {/* Responsible AI Video detail routes */}
              <Route path="/responsible-use-of-ai/videos/:nid" element={wrapRoute(<VideoDetailPage />)} />
            </Route>
          </Route>

          {/* 404 route - public */}
          <Route path={ROUTES.NOT_FOUND} element={wrapRoute(<NotFound />)} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppRoutes;
